import React, { useEffect, useState } from "react";
import { t } from "utils/i18n";
import { Fa } from "composants/Icon";
import Modal from "composants/Modal/Modal";
import toaster from "composants/toaster/toaster";
import {
  assistTextWriting,
  isRewriteAction,
  TextWritingAction,
  TRANSLATE_LANGUAGES,
  TranslateLangCode
} from "api/textWritingAssist";

const ACTION_TITLES: Record<TextWritingAction, string> = {
  translate: "commun_ia_traduire",
  analyze: "commun_ia_analyser",
  longer: "commun_ia_plus_long",
  shorter: "commun_ia_plus_court",
  explain: "commun_ia_expliquer",
  spellcheck: "commun_ia_corriger"
};

const ACTION_DEFAULTS: Record<TextWritingAction, string> = {
  translate: "Traduire",
  analyze: "Analyser le texte",
  longer: "Écrire plus long",
  shorter: "Écrire plus court",
  explain: "Expliquer",
  spellcheck: "Corriger l'orthographe"
};

/**
 * Convertit un markdown basique en HTML pour l'aperçu analyse / explication.
 */
function markdownToHtml(markdown: string): string {
  if (!markdown) return "";
  let html = markdown;
  html = html.replace(/## (.*?)(\n|$)/g, '<h2 class="text-lg font-bold my-2">$1</h2>');
  html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/^- (.*?)$/gm, '<li class="ml-4 py-0.5">$1</li>');
  html = html.replace(/(<li.*?>.*?<\/li>(\n|$))+/g, '<ul class="my-1 mb-2">$&</ul>');
  html = html.replace(/\n\n/g, '</p><p class="my-1">');
  html = `<div class="leading-snug text-sm"><p class="my-1">${html}</p></div>`;
  html = html.replace(/<p class="my-1"><ul/g, "<ul");
  html = html.replace(/<\/ul><\/p>/g, "</ul>");
  html = html.replace(/<p class="my-1"><h2/g, "<h2");
  html = html.replace(/<\/h2><\/p>/g, "</h2>");
  return html;
}

export interface AiWritingAssistModalProps {
  action: TextWritingAction;
  sourceText: string;
  onAccept(result: string): void;
  onClose(): void;
}

const AiWritingAssistModal: React.FC<AiWritingAssistModalProps> = ({
  action,
  sourceText,
  onAccept,
  onClose
}) => {
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [targetLang, setTargetLang] = useState<TranslateLangCode>("EN");
  const rewrite = isRewriteAction(action);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const generated = await assistTextWriting({
          action,
          text: sourceText,
          targetLang: action === "translate" ? targetLang : undefined
        });
        if (!cancelled) {
          setResult(generated);
        }
      } catch (err) {
        if (cancelled) {
          return;
        }
        const message =
          err instanceof Error
            ? err.message
            : t("commun_ia_erreur", { defaultValue: "Une erreur est survenue lors de l'assistance IA." });
        setError(message);
        toaster.error(message);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [action, sourceText, targetLang]);

  const title = t(ACTION_TITLES[action], { defaultValue: ACTION_DEFAULTS[action] });

  const canAccept = rewrite && !!result && !loading && !error;

  return (
    <Modal
      show
      title={title}
      onClose={() => onClose()}
      onValidate={() => {
        if (canAccept) {
          onAccept(result);
        } else {
          onClose();
        }
      }}
      hideValidate={rewrite ? !canAccept : false}
      hideCancel={!rewrite}
      validateComponent={
        rewrite ? (
          <button
            className="button is-success"
            disabled={!canAccept}
            onClick={() => onAccept(result)}
          >
            {t("commun_accepter", { defaultValue: "Accepter" })}
          </button>
        ) : (
          <button className="button is-success" onClick={() => onClose()}>
            {t("commun_fermer", { defaultValue: "Fermer" })}
          </button>
        )
      }
      disableFooterButton={loading}
      width="640px"
      minHeight="200px"
    >
      <div className="p-4 space-y-4">
        {action === "translate" && (
          <div className="field">
            <label className="label" htmlFor="ai-translate-lang">
              {t("commun_ia_choisir_langue", { defaultValue: "Choisir la langue" })}
            </label>
            <div className="control">
              <div className="select is-fullwidth">
                <select
                  id="ai-translate-lang"
                  value={targetLang}
                  disabled={loading}
                  onChange={e => setTargetLang(e.target.value as TranslateLangCode)}
                >
                  {TRANSLATE_LANGUAGES.map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center p-8">
            <Fa icon="spinner" spin size="2x" className="text-primary mb-4" />
            <p className="text-gray-600">
              {t("commun_ia_en_cours", { defaultValue: "Génération en cours…" })}
            </p>
          </div>
        ) : error ? (
          <div className="p-4 has-background-danger-light has-text-danger">
            <Fa icon="exclamation-triangle" className="mr-2" />
            {error}
          </div>
        ) : (
          <div className="space-y-2">
            <p className="has-text-grey is-size-7">
              {t("commun_ia_apercu", { defaultValue: "Aperçu" })}
            </p>
            {rewrite ? (
              <div
                className="p-4 border rounded"
                style={{
                  whiteSpace: "pre-wrap",
                  maxHeight: "50vh",
                  overflow: "auto",
                  background: "#fff"
                }}
              >
                {result}
              </div>
            ) : (
              <div
                className="p-4 border rounded"
                style={{ maxHeight: "50vh", overflow: "auto", background: "#fff" }}
                dangerouslySetInnerHTML={{ __html: markdownToHtml(result) }}
              />
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AiWritingAssistModal;
